import React, { useEffect, useState } from 'react';
import { Loader2, Save, Sparkles, Sun, Cloud, Newspaper, Home, ShieldCheck } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdminHeroes — one screen with 7 sub-forms, each corresponding to a page-hero
 * or hero-adjacent singleton stored under `/api/admin/settings/<key>`.
 *
 * Each sub-form has its own local dirty state and Save button so the operator
 * can iterate on one hero without touching the others.
 */
const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children, hint }) => (
  <div>
    <label
      dir={dir}
      className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
  </div>
);

/** Bilingual pair input helper. `field` is a key on the value object,
 *  which is expected to be `{ ar, en }`. */
const BilingualPair = ({ value, onChange, labelKey, isAr, dirHint = 'text' }) => {
  const v = value || {};
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Field label={isAr ? `${labelKey} (عربي)` : `${labelKey} (Arabic)`} dir="rtl">
        <input dir="rtl" className={inputCls} value={v.ar || ''} onChange={(e) => onChange({ ...v, ar: e.target.value })} />
      </Field>
      <Field label={isAr ? `${labelKey} (إنجليزي)` : `${labelKey} (English)`} dir="ltr">
        <input dir="ltr" className={inputCls} value={v.en || ''} onChange={(e) => onChange({ ...v, en: e.target.value })} />
      </Field>
      {dirHint === 'textarea' && null}
    </div>
  );
};

const BilingualBody = ({ value, onChange, labelKey, isAr, rows = 4 }) => {
  const v = value || {};
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Field label={isAr ? `${labelKey} (عربي)` : `${labelKey} (Arabic)`} dir="rtl">
        <textarea rows={rows} dir="rtl" className={inputCls} value={v.ar || ''} onChange={(e) => onChange({ ...v, ar: e.target.value })} />
      </Field>
      <Field label={isAr ? `${labelKey} (إنجليزي)` : `${labelKey} (English)`} dir="ltr">
        <textarea rows={rows} dir="ltr" className={inputCls} value={v.en || ''} onChange={(e) => onChange({ ...v, en: e.target.value })} />
      </Field>
    </div>
  );
};

const HeroCard = ({ Icon, titleAr, titleEn, isAr, children, onSave, saving, testId, subtitle }) => (
  <section
    data-testid={testId}
    className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
  >
    <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15 border border-brand-blue/25 flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
      </span>
      <div className="flex-1">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{isAr ? titleAr : titleEn}</h2>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
    </div>
    {children}
    <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
      <button
        type="button"
        data-testid={`${testId}-save`}
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {isAr ? 'حفظ' : 'Save'}
      </button>
    </div>
  </section>
);

// The 7 setting keys managed by this screen.
const KEYS = [
  'hero', 'services_hero', 'accreditations_hero',
  'news_hero', 'vision2030_hero', 'responsibility_extra',
];

const AdminHeroes = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  // Store all keys in a single map so re-renders are cheap.
  const [data, setData] = useState(() => Object.fromEntries(KEYS.map((k) => [k, {}])));
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const set = (key, updater) => {
    setData((prev) => ({ ...prev, [key]: typeof updater === 'function' ? updater(prev[key] || {}) : updater }));
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(KEYS.map((k) => adminApi.get(`/settings/${k}`)));
      const next = {};
      KEYS.forEach((k, i) => { next[k] = (results[i].data && results[i].data.value) || {}; });
      setData(next);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const saveOne = async (key) => {
    setSavingKey(key); setError(''); setMsg('');
    try {
      await adminApi.put(`/settings/${key}`, { value: data[key] || {} });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingKey(''); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  const hero      = data.hero      || {};
  const serv      = data.services_hero || {};
  const acc       = data.accreditations_hero || {};
  const news      = data.news_hero  || {};
  const v2030     = data.vision2030_hero || {};
  const respExtra = data.responsibility_extra || {};

  const panels = Array.isArray(news.panels) ? news.panels : [];
  const setPanel = (idx, patch) => set('news_hero', (prev) => {
    const list = Array.isArray(prev.panels) ? [...prev.panels] : [];
    while (list.length <= idx) list.push({});
    list[idx] = { ...list[idx], ...patch };
    return { ...prev, panels: list };
  });

  return (
    <div className="space-y-6" data-testid="admin-heroes-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'أقسام البطل (Heroes)' : 'Page Heroes'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr
            ? 'حرّر عناوين وأوصاف وصور خلفية أقسام البطل لكل صفحة. اترك أي حقل فارغاً لاستخدام النص الافتراضي.'
            : 'Edit titles, subtitles, and background images for each page-hero. Leave a field blank to use the built-in default.'}
        </p>
      </div>
      {error && <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
      {msg && <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{msg}</div>}

      {/* ---------- HOME HERO ---------- */}
      <HeroCard Icon={Home} isAr={isAr} testId="hero-home" onSave={() => saveOne('hero')} saving={savingKey === 'hero'} titleAr="الصفحة الرئيسية — قسم البطل" titleEn="Homepage · Hero">
        <BilingualPair value={hero.title}    onChange={(v) => set('hero', { ...hero, title: v })}    labelKey={isAr ? 'العنوان' : 'Title'}    isAr={isAr} />
        <BilingualPair value={hero.subtitle} onChange={(v) => set('hero', { ...hero, subtitle: v })} labelKey={isAr ? 'العنوان الفرعي' : 'Subtitle'} isAr={isAr} />
        <BilingualPair value={hero.cta_text} onChange={(v) => set('hero', { ...hero, cta_text: v })} labelKey={isAr ? 'نص الزر' : 'CTA text'} isAr={isAr} />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'خلفية (Light mode) — URL' : 'Background (Light) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={hero.background_light || ''} onChange={(e) => set('hero', { ...hero, background_light: e.target.value })} />
          </Field>
          <Field label={isAr ? 'خلفية (Dark mode) — URL' : 'Background (Dark) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={hero.background_dark || ''} onChange={(e) => set('hero', { ...hero, background_dark: e.target.value })} />
          </Field>
        </div>
      </HeroCard>

      {/* ---------- SERVICES HERO ---------- */}
      <HeroCard Icon={Sun} isAr={isAr} testId="hero-services" onSave={() => saveOne('services_hero')} saving={savingKey === 'services_hero'} titleAr="صفحة الخدمات — قسم البطل" titleEn="Services page · Hero">
        <BilingualPair value={serv.eyebrow}     onChange={(v) => set('services_hero', { ...serv, eyebrow: v })}     labelKey={isAr ? 'الشعار الصغير' : 'Eyebrow'} isAr={isAr} />
        <BilingualPair value={serv.title_line1} onChange={(v) => set('services_hero', { ...serv, title_line1: v })} labelKey={isAr ? 'السطر الأول' : 'Title line 1'} isAr={isAr} />
        <BilingualPair value={serv.title_line2} onChange={(v) => set('services_hero', { ...serv, title_line2: v })} labelKey={isAr ? 'السطر الثاني (متدرج)' : 'Title line 2 (gradient)'} isAr={isAr} />
        <BilingualBody value={serv.subtitle}    onChange={(v) => set('services_hero', { ...serv, subtitle: v })}    labelKey={isAr ? 'العنوان الفرعي' : 'Subtitle'} isAr={isAr} rows={3} />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'صورة خلفية البطل — URL' : 'Hero background — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={serv.background || ''} onChange={(e) => set('services_hero', { ...serv, background: e.target.value })} />
          </Field>
          <Field label={isAr ? 'خلفية القسم (سكرول ثابت) — URL' : 'Section background (parallax) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={serv.section_background || ''} onChange={(e) => set('services_hero', { ...serv, section_background: e.target.value })} />
          </Field>
        </div>
      </HeroCard>

      {/* ---------- ACCREDITATIONS HERO ---------- */}
      <HeroCard Icon={Cloud} isAr={isAr} testId="hero-accreditations" onSave={() => saveOne('accreditations_hero')} saving={savingKey === 'accreditations_hero'} titleAr="صفحة الاعتمادات — قسم البطل" titleEn="Accreditations page · Hero">
        <BilingualPair value={acc.eyebrow}     onChange={(v) => set('accreditations_hero', { ...acc, eyebrow: v })}     labelKey={isAr ? 'الشعار الصغير' : 'Eyebrow'} isAr={isAr} />
        <BilingualPair value={acc.title_line1} onChange={(v) => set('accreditations_hero', { ...acc, title_line1: v })} labelKey={isAr ? 'السطر الأول' : 'Title line 1'} isAr={isAr} />
        <BilingualPair value={acc.title_line2} onChange={(v) => set('accreditations_hero', { ...acc, title_line2: v })} labelKey={isAr ? 'السطر الثاني (متدرج)' : 'Title line 2 (gradient)'} isAr={isAr} />
        <BilingualBody value={acc.subtitle}    onChange={(v) => set('accreditations_hero', { ...acc, subtitle: v })}    labelKey={isAr ? 'العنوان الفرعي' : 'Subtitle'} isAr={isAr} rows={3} />
      </HeroCard>

      {/* ---------- NEWS HERO ---------- */}
      <HeroCard Icon={Newspaper} isAr={isAr} testId="hero-news" onSave={() => saveOne('news_hero')} saving={savingKey === 'news_hero'} titleAr="صفحة الأخبار — قسم البطل" titleEn="News page · Hero"
        subtitle={isAr ? 'الترتيب: 0-السماء، 1-الغابات، 2-الصحراء، 3-البحر.' : 'Order: 0-Sky, 1-Forest, 2-Desert, 3-Sea.'}>
        <BilingualPair value={news.eyebrow}         onChange={(v) => set('news_hero', { ...news, eyebrow: v })}         labelKey={isAr ? 'الشعار الصغير' : 'Eyebrow'} isAr={isAr} />
        <BilingualPair value={news.title_prefix}    onChange={(v) => set('news_hero', { ...news, title_prefix: v })}    labelKey={isAr ? 'العنوان (البداية)' : 'Title (prefix)'} isAr={isAr} />
        <BilingualPair value={news.title_highlight} onChange={(v) => set('news_hero', { ...news, title_highlight: v })} labelKey={isAr ? 'العنوان (متدرج)' : 'Title (gradient)'} isAr={isAr} />
        <BilingualBody value={news.subtitle}        onChange={(v) => set('news_hero', { ...news, subtitle: v })}        labelKey={isAr ? 'العنوان الفرعي' : 'Subtitle'} isAr={isAr} rows={3} />
        <div className="pt-2 space-y-3">
          <p className={`text-xs font-semibold text-slate-600 dark:text-slate-300 ${isAr ? 'text-right' : ''}`}>
            {isAr ? 'صور اللوحات الأربع (اترك فارغاً للاحتفاظ بالافتراضية)' : 'Four-panel image overrides (blank = keep default)'}
          </p>
          {['Sky · السماء', 'Forest · الغابات', 'Desert · الصحراء', 'Sea · البحر'].map((label, idx) => (
            <div key={idx} className="grid md:grid-cols-2 gap-3">
              <Field label={`${label} — Light URL`} dir="ltr">
                <input dir="ltr" className={inputCls} value={(panels[idx] && panels[idx].img_light) || ''} onChange={(e) => setPanel(idx, { img_light: e.target.value })} />
              </Field>
              <Field label={`${label} — Dark URL`} dir="ltr">
                <input dir="ltr" className={inputCls} value={(panels[idx] && panels[idx].img_dark) || ''} onChange={(e) => setPanel(idx, { img_dark: e.target.value })} />
              </Field>
            </div>
          ))}
        </div>
      </HeroCard>

      {/* ---------- VISION 2030 HERO ---------- */}
      <HeroCard Icon={Sparkles} isAr={isAr} testId="hero-vision2030" onSave={() => saveOne('vision2030_hero')} saving={savingKey === 'vision2030_hero'} titleAr="رؤية 2030 — التعهد والشعار" titleEn="Vision 2030 · Pledge & Logo">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'شعار Vision 2030 (Light) — URL' : 'Vision 2030 logo (Light) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={v2030.logo_light || ''} onChange={(e) => set('vision2030_hero', { ...v2030, logo_light: e.target.value })} />
          </Field>
          <Field label={isAr ? 'شعار Vision 2030 (Dark) — URL' : 'Vision 2030 logo (Dark) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={v2030.logo_dark || ''} onChange={(e) => set('vision2030_hero', { ...v2030, logo_dark: e.target.value })} />
          </Field>
        </div>
        <BilingualPair value={v2030.pledge_title} onChange={(v) => set('vision2030_hero', { ...v2030, pledge_title: v })} labelKey={isAr ? 'عنوان التعهد' : 'Pledge title'} isAr={isAr} />
        <BilingualBody value={v2030.pledge_body}  onChange={(v) => set('vision2030_hero', { ...v2030, pledge_body: v })}  labelKey={isAr ? 'نص التعهد' : 'Pledge body'} isAr={isAr} rows={5} />
      </HeroCard>

      {/* ---------- RESPONSIBILITY EXTRA ---------- */}
      <HeroCard Icon={ShieldCheck} isAr={isAr} testId="hero-responsibility" onSave={() => saveOne('responsibility_extra')} saving={savingKey === 'responsibility_extra'} titleAr="المسؤولية الوطنية — المبدأ والبيان" titleEn="Responsibility · Principle & Manifesto">
        <BilingualPair value={respExtra.principle_title} onChange={(v) => set('responsibility_extra', { ...respExtra, principle_title: v })} labelKey={isAr ? 'عنوان المبدأ' : 'Principle title'} isAr={isAr} />
        <BilingualBody value={respExtra.principle_body}  onChange={(v) => set('responsibility_extra', { ...respExtra, principle_body: v })}  labelKey={isAr ? 'نص المبدأ' : 'Principle body'} isAr={isAr} rows={4} />
        <BilingualBody value={respExtra.manifesto_body}  onChange={(v) => set('responsibility_extra', { ...respExtra, manifesto_body: v })}  labelKey={isAr ? 'نص البيان' : 'Manifesto body'} isAr={isAr} rows={5} />
      </HeroCard>
    </div>
  );
};

export default AdminHeroes;
